import { useCallback } from "react";
import {
  ArrowDownIcon,
  ArrowUpIcon,
  DocumentDuplicateIcon,
  EyeIcon,
  FilterIcon,
  LightBulbIcon,
  PencilIcon,
  XIcon,
} from "@heroicons/react/outline";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  EyeClosedIcon,
} from "@radix-ui/react-icons";

import {
  useColumnSearch,
  useSearch,
} from "@/components/ui/table/Search/search-context";
import { useCollapsibleColumnContext } from "@/components/ui/table/collapsible-columns/useCollapsibleColumns";
import { ColumnType, ReportColumn } from "@/types/evaluate";
import {
  ContextMenuItems,
  ContextMenuVariant,
} from "@/components/ui/ContextMenu/types";
import { useColumnDuplication } from "./useColumnDuplication";
import { useColumnMovement } from "./useColumnMovement";
import { useColumnDeletion } from "./useColumnDeletion";
import { useColumnSorting } from "./useColumnSorting";

interface UseHeaderContextMenuProps {
  column: ReportColumn;
  reportId: number;
  availableColumns: ReportColumn[];
  isEditable: boolean;
  onOpenModal: () => void;
}

export const useHeaderContextMenu = ({
  column,
  reportId,
  availableColumns,
  isEditable,
  onOpenModal,
}: UseHeaderContextMenuProps) => {
  const [, showFilterInput, setShowFilterInput] = useColumnSearch(
    column?.id!.toString(),
  );
  const { toggleColumn, toggleOthers } = useCollapsibleColumnContext();
  const { enabled } = useSearch();

  const { handleDuplicateColumn } = useColumnDuplication({ reportId });
  const { handleMoveColumn } = useColumnMovement({ reportId });
  const {
    isDeleteConfirmationOpen,
    setIsDeleteConfirmationOpen,
    handleDeleteColumn,
    handleConfirmDelete,
    handleCancelDelete,
  } = useColumnDeletion({ reportId });
  const { handleSort } = useColumnSorting();

  const handleShowFilter = useCallback(() => {
    setShowFilterInput(true);
  }, [setShowFilterInput]);

  const handleToggleColumn = useCallback(() => {
    toggleColumn(column.name);
  }, [column.name, toggleColumn]);

  const handleToggleOthers = useCallback(() => {
    toggleOthers(column.name);
  }, [column.name, toggleOthers]);

  const availablePreviousColumns = availableColumns.filter(
    (c: ReportColumn) => c.position < column.position,
  );

  const contextMenuItems: ContextMenuItems<ReportColumn>[] = [
    {
      id: "inspect",
      label: "Inspect",
      icon: EyeIcon,
      onClick: onOpenModal,
      hidden: isEditable || column.column_type === ColumnType.DATASET,
    },
    {
      id: "edit",
      label: "Edit",
      icon: PencilIcon,
      onClick: onOpenModal,
      hidden: !isEditable,
    },
    {
      id: "duplicate",
      label: "Duplicate",
      icon: DocumentDuplicateIcon,
      onClick: () => handleDuplicateColumn(column),
      hidden: !isEditable,
    },
    {
      id: "hide",
      label: "Hide",
      icon: EyeClosedIcon,
      onClick: handleToggleColumn,
    },
    {
      id: "hide-others",
      label: "Hide everything else",
      icon: LightBulbIcon,
      onClick: handleToggleOthers,
    },
    {
      id: "move-left",
      label: "Move left",
      icon: ArrowLeftIcon,
      onClick: () => handleMoveColumn(column, "left"),
      hidden:
        !isEditable ||
        availablePreviousColumns.length === 0 ||
        availablePreviousColumns[availablePreviousColumns.length - 1]
          .column_type === ColumnType.DATASET,
    },
    {
      id: "move-right",
      label: "Move right",
      icon: ArrowRightIcon,
      onClick: () => handleMoveColumn(column, "right"),
      hidden:
        !isEditable ||
        availableColumns.length === 0 ||
        availableColumns.indexOf(column) >= availableColumns.length - 1,
    },
    {
      id: "filter",
      label: "Filter",
      icon: FilterIcon,
      onClick: handleShowFilter,
      hidden: !enabled,
    },
    {
      id: "sort-asc",
      label: "Sort ascending",
      icon: ArrowUpIcon,
      onClick: () => handleSort(column, "asc"),
      hidden: !enabled,
    },
    {
      id: "sort-desc",
      label: "Sort descending",
      icon: ArrowDownIcon,
      onClick: () => handleSort(column, "desc"),
      hidden: !enabled,
    },
    {
      id: "delete",
      label: "Delete",
      icon: XIcon,
      onClick: () => handleDeleteColumn(column),
      variant: ContextMenuVariant.DESTRUCTIVE,
      hidden: !isEditable,
    },
  ];

  return {
    contextMenuItems,
    isDeleteConfirmationOpen,
    showFilterInput,
    setShowFilterInput,
    setIsDeleteConfirmationOpen,
    handleConfirmDelete: () => handleConfirmDelete(column),
    handleCancelDelete,
    column,
  };
};
